// src/context/AuthContext.jsx
// App-wide auth + student-profile provider.
//
// Role rule (single source of truth):
//   - a signed-in user WITH a students/{uid} doc  -> role "student"
//   - a signed-in user WITHOUT one                -> role "admin"
//   - signed out                                  -> role null
//
// This is what gates the two login doors (see StudentLogin / Login) and the
// student-only routes (see App.js StudentRoute).

import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { onAuthStateChanged, signOut } from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";
import { auth, db } from "../components/firebase/firebase";

const AuthContext = createContext({
  user: null,
  profile: null,
  role: null,
  loading: true,
  refreshProfile: async () => {},
  logout: async () => {},
});

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [role, setRole] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadProfile = useCallback(async (u) => {
    if (!u) {
      setProfile(null);
      setRole(null);
      return;
    }
    try {
      const snap = await getDoc(doc(db, "students", u.uid));
      if (snap.exists()) {
        setProfile({ id: snap.id, ...snap.data() });
        setRole("student");
      } else {
        setProfile(null);
        setRole("admin");
      }
    } catch (e) {
      console.error("Failed to load student profile", e);
      setProfile(null);
      setRole("admin");
    }
  }, []);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (u) => {
      setUser(u);
      await loadProfile(u);
      setLoading(false);
    });
    return unsub;
  }, [loadProfile]);

  const refreshProfile = useCallback(async () => {
    if (auth.currentUser) await loadProfile(auth.currentUser);
  }, [loadProfile]);

  const logout = useCallback(async () => {
    // Clear admin token too, in case it lingers from an admin session.
    try { localStorage.removeItem("accessToken"); } catch (_) {}
    await signOut(auth);
  }, []);

  return (
    <AuthContext.Provider value={{ user, profile, role, loading, refreshProfile, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);

export default AuthContext;
